
  import { defineComponent, ref, onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import {
    IndicesPropertyCategoryItemObject,
    IndicesPropertyCategoryViewFlag,
  } from '@/store/modules/Indices/IndicesPropertyCategoriesModule';

  export default defineComponent({
    name: 'indices-property-categories-listing',
    components: {
      Datatable,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(
          translate('indicesPropertyCategoriesListing'),
          [translate('indices')]
        );
      });

      await store.dispatch(Actions.GET_INDICES_PROPERTY_CATEGORIES);
      const loading = ref(false);

      const tableHeader = ref([
        {
          name: translate('idColumn'),
          key: 'id',
          sortable: true,
        },
        {
          name: translate('titleAr'),
          key: 'titleAr',
          sortable: true,
        },
        {
          name: translate('titleEn'),
          key: 'titleEn',
          sortable: true,
        },
        {
          name: translate('slug'),
          key: 'slug',
          sortable: true,
        },
        {
          name: translate('disablePriceAreas'),
          key: 'disablePriceAreas',
          sortable: true,
        },
        {
          name: translate('disableVolumeAreas'),
          key: 'disableVolumeAreas',
          sortable: true,
        },
        {
          name: translate('volumeIndexTicker'),
          key: 'volume_index_ticker',
          sortable: true,
        },
        {
          name: translate('priceIndexView'),
          key: 'price_index_view_active',
        },
        {
          name: translate('volumeIndexView'),
          key: 'volume_index_view_active',
        },
        {
          name: translate('ppmView'),
          key: 'ppm_view_active',
        },
        {
          name: translate('noOfDealsView'),
          key: 'no_of_deals_view_active',
        },
        {
          name: translate('status'),
          key: 'isActive',
          sortable: true,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);

      const toggleStatus = async (item) => {
        const deletedMessage = item.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(
            Actions.DELETE_INDICES_PROPERTY_CATEGORY,
            item.id
          );
          await store.dispatch(Actions.GET_INDICES_PROPERTY_CATEGORIES);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = item.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const togglingId = ref<number | null>(null);

      // Saves one app view flag straight away; reverts the switch on failure.
      const toggleViewFlag = async (
        item: IndicesPropertyCategoryItemObject,
        flag: IndicesPropertyCategoryViewFlag
      ) => {
        const value = !item[flag];
        item[flag] = value;
        togglingId.value = item.id;
        await store.dispatch(
          Actions.UPDATE_INDICES_PROPERTY_CATEGORY_VIEW_FLAGS,
          {
            id: item.id,
            data: { [flag]: value },
          }
        );
        togglingId.value = null;
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];
        if (error) {
          item[flag] = !value;
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } else {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            text: translate('SUCCESSFULLY_UPDATED_VIEW_FLAG'),
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
          });
        }
      };

      const editItem = (id) => {
        router.push({
          name: 'indices-property-categories-editing',
          params: { id },
        });
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(
          Actions.UPDATE_INDICES_PROPERTY_CATEGORY_CURRENT_PAGE,
          val
        );
        await store.dispatch(Actions.GET_INDICES_PROPERTY_CATEGORIES);
        loading.value = false;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tableData: computed(() => store.getters.indicesPropertyCategoryList),
        tableHeader,
        toggleStatus,
        toggleViewFlag,
        togglingId,
        translate,
        totalItems: computed(
          () => store.getters.indicesPropertyCategoryListCount
        ),
        pageChanged,
        goTo,
        editItem,
        loading,
        can,
      };
    },
  });
